import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-shirt-line',
  'Pants/Shorts': 'ri-shirt-line',
  'Headwear': 'ri-sun-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-circle-line',
  'Jersey': 'ri-shirt-line',
  'Accessories': 'ri-vip-diamond-line',
  'Bags': 'ri-shopping-bag-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              oopbuy<span className="text-primary-500">spread</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Startseite
            </Link>

            <Link
              to="/artikel/oopbuy-spreadsheet-guide"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Einkaufsguide
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className="ri-grid-line text-sm"></i>
                </span>
                Kategorien
                <span className={`w-3 h-3 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                        <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="#guides" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">Guides</a>
            <a href="#vergleich" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">Vergleich</a>
            <Link to="/artikel/oopbuy-sicherheit" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">Sicherheit</Link>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Spreadsheet
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            aria-label="Menü öffnen"
          >
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">

            {/* Main navigation */}
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileOpen(false)}
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Startseite
            </Link>

            <Link
              to="/artikel/oopbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setIsMobileOpen(false)}
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Einkaufsguide
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>

            {/* Category header */}
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 pt-1 pb-2">Kategorien</p>

            <div className="grid grid-cols-1 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                  onClick={() => setIsMobileOpen(false)}
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200/50 text-foreground-500 flex-shrink-0">
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-sm`}></i>
                  </span>
                  <span>{cat.name}</span>
                  <span className="ml-auto w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/70 my-2"></div>

            <a href="#guides" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors" onClick={() => setIsMobileOpen(false)}>
              <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                <i className="ri-article-line text-sm"></i>
              </span>
              Guides
            </a>
            <a href="#vergleich" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors" onClick={() => setIsMobileOpen(false)}>
              <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                <i className="ri-scales-line text-sm"></i>
              </span>
              Vergleich
            </a>
            <Link to="/artikel/oopbuy-sicherheit" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors" onClick={() => setIsMobileOpen(false)}>
              <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                <i className="ri-shield-check-line text-sm"></i>
              </span>
              Sicherheit
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 mx-3 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              onClick={() => setIsMobileOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-sm"></i>
              </span>
              Zum Spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}